import type { ReactNode } from 'react';
import { BarChart3 } from 'lucide-react';
import type { Build } from '@/components/smartai/api/getBuildList';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/ui/status-badge';
import { cn } from '@/lib/utils';
import { onboardingPageClass } from '@/components/smartai/ComponentOnboarding/onboarding-layout';

export const buildDiffPageClass = onboardingPageClass;

export const buildDiffCardClass =
  'min-w-0 rounded-[14px] border border-[var(--inv-border)] bg-[var(--inv-surface)] p-4 shadow-[var(--inv-shadow,0_1px_2px_rgba(43,33,24,0.04))]';

/** Summary metrics (4 items) — 2×2 on laptops, 4-up on wide panes. */
export const buildDiffMetricsGridClass =
  'mb-5 grid w-full min-w-0 grid-cols-2 gap-3 min-[1280px]:grid-cols-4 min-[1280px]:gap-4';

/** Entity summary cards (up to 5) — never 4-up on laptop (inner 3-stat chips collide). */
export const buildDiffEntityGridClass =
  'mb-6 grid w-full min-w-0 grid-cols-1 gap-3 min-[700px]:grid-cols-2 min-[1500px]:grid-cols-3 min-[1800px]:grid-cols-5';

interface BuildDiffCardHeaderProps {
  title: string;
  subtitle?: string;
  action?: ReactNode;
  className?: string;
}

export function BuildDiffCardHeader({
  title,
  subtitle,
  action,
  className,
}: BuildDiffCardHeaderProps) {
  return (
    <div
      className={cn(
        'mb-4 flex flex-wrap items-start justify-between gap-3',
        className,
      )}>
      <div>
        <h2 className="mb-1 font-serif text-[1.0625rem] font-semibold text-[var(--inv-text)]">
          {title}
        </h2>
        {subtitle ? (
          <p className="m-0 text-[0.8125rem] leading-normal text-[var(--inv-text-secondary)]">
            {subtitle}
          </p>
        ) : null}
      </div>
      {action}
    </div>
  );
}

export function BuildStatusBadge({ result }: { result: Build['result'] }) {
  const label =
    result === 'succeeded'
      ? 'Passed'
      : result === 'partiallySucceeded'
        ? 'Partially succeeded'
        : result === 'canceled'
          ? 'Canceled'
          : result === 'failed'
            ? 'Failed'
            : result;

  const variant =
    result === 'succeeded'
      ? 'success'
      : result === 'failed' || result === 'canceled'
        ? 'danger'
        : result === 'partiallySucceeded'
          ? 'warning'
          : 'info-outlined';

  return (
    <StatusBadge
      value={label}
      variant={variant}
      className="w-fit self-start text-[0.6875rem] capitalize tracking-wide"
    />
  );
}

interface ModeToggleProps {
  byDate: boolean;
  onChange: (byDate: boolean) => void;
}

export function ModeToggle({ byDate, onChange }: ModeToggleProps) {
  return (
    <div
      className="inline-flex rounded-[10px] border border-solid border-[var(--inv-border-soft)] bg-[var(--inv-surface-muted)] p-0.5"
      role="group"
      aria-label="Comparison mode">
      <Button
        type="button"
        variant="ghost"
        className={cn(
          'h-auto rounded-lg px-[0.85rem] py-1.5 text-[0.8125rem] hover:bg-[var(--inv-surface)]',
          !byDate
            ? 'bg-[var(--inv-surface)] text-[var(--inv-accent)] shadow-[var(--inv-shadow,0_1px_2px_rgba(43,33,24,0.06))]'
            : 'text-[var(--inv-text-secondary)] hover:text-[var(--inv-text)]',
        )}
        onClick={() => onChange(false)}>
        By build
      </Button>
      <Button
        type="button"
        variant="ghost"
        className={cn(
          'h-auto rounded-lg px-[0.85rem] py-1.5 text-[0.8125rem] hover:bg-[var(--inv-surface)]',
          byDate
            ? 'bg-[var(--inv-surface)] text-[var(--inv-accent)] shadow-[var(--inv-shadow,0_1px_2px_rgba(43,33,24,0.06))]'
            : 'text-[var(--inv-text-secondary)] hover:text-[var(--inv-text)]',
        )}
        onClick={() => onChange(true)}>
        By date
      </Button>
    </div>
  );
}

export function BuildDetailsPanel({ build }: { build: Build }) {
  const branch = (build.sourceBranch || 'main').replace(/^refs\/heads\//, '');

  return (
    <div className="flex flex-col gap-2 rounded-[10px] border border-solid border-[var(--inv-border-soft)] bg-[var(--inv-surface-muted)] px-4 py-3.5">
      <div className="flex justify-between gap-3 text-[0.8125rem]">
        <span className="text-[0.6875rem] font-semibold uppercase tracking-wide text-[var(--inv-text-muted)]">
          Build
        </span>
        <span className="text-right font-tabular text-[var(--inv-text)]">
          #{build.buildNumber}
        </span>
      </div>
      <div className="flex justify-between gap-3 text-[0.8125rem]">
        <span className="text-[0.6875rem] font-semibold uppercase tracking-wide text-[var(--inv-text-muted)]">
          Queued
        </span>
        <span className="break-words text-right font-tabular text-[var(--inv-text)]">
          {new Date(build.queueTime).toLocaleString()}
        </span>
      </div>
      <div className="flex justify-between gap-3 text-[0.8125rem]">
        <span className="text-[0.6875rem] font-semibold uppercase tracking-wide text-[var(--inv-text-muted)]">
          Branch
        </span>
        <span className="break-words text-right text-[var(--inv-text)]">
          {branch}
        </span>
      </div>
      <BuildStatusBadge result={build.result} />
    </div>
  );
}

export interface TimelineEventItem {
  id: string;
  title: string;
  description: string;
  time: string;
  build?: Build;
  isSelected?: boolean;
}

interface ActivityTimelineProps {
  events: TimelineEventItem[];
}

export function ActivityTimeline({ events }: ActivityTimelineProps) {
  return (
    <div className="relative flex-1 overflow-y-auto pr-1">
      <div
        className="absolute bottom-0 left-[18px] top-0 w-0.5 bg-[var(--inv-border-soft)]"
        aria-hidden
      />
      <div className="flex flex-col gap-3">
        {events.map(event => (
          <div key={event.id} className="relative flex gap-3.5">
            <div className="z-[1] flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--inv-accent)] text-[var(--inv-on-accent)] shadow-[var(--inv-shadow,0_1px_2px_rgba(43,33,24,0.08))]">
              <BarChart3 className="h-4 w-4" aria-hidden />
            </div>
            <div
              className={cn(
                'min-w-0 flex-1 rounded-[10px] border border-solid border-[var(--inv-border-soft)] border-l-[3px] border-l-[var(--inv-accent)] bg-[var(--inv-surface-muted)] px-3.5 py-3 transition-colors',
                event.isSelected &&
                  'border-[var(--inv-accent-soft)] bg-[var(--inv-accent-ring,rgba(0,70,184,0.06))]',
              )}>
              <div className="flex items-start justify-between gap-3 max-[768px]:flex-col max-[768px]:gap-2">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="text-sm font-semibold text-[var(--inv-text)]">
                      {event.title}
                    </span>
                    {event.build ? (
                      <BuildStatusBadge result={event.build.result} />
                    ) : null}
                  </div>
                  <p className="m-0 mt-1 text-xs leading-snug text-[var(--inv-text-secondary)]">
                    {event.description}
                  </p>
                </div>
                {event.time ? (
                  <time className="shrink-0 whitespace-nowrap text-[0.6875rem] text-[var(--inv-text-muted)]">
                    {event.time}
                  </time>
                ) : null}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function FilterPill({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center whitespace-nowrap rounded-full border border-solid border-[var(--inv-border-soft)] bg-[var(--inv-accent-soft)] px-2.5 py-1 text-[0.6875rem] font-semibold uppercase tracking-wide text-[var(--inv-accent)]">
      {children}
    </span>
  );
}

/** File-level expand: entity breakdown panel (build diff). */
export const buildDiffEntityDetailsPanelClass = cn(
  buildDiffCardClass,
  'mt-4 border-t-4 border-t-[var(--inv-accent-soft)]',
);

export const buildDiffEntityDetailsHeaderClass =
  'mb-4 flex items-start justify-between gap-3';

export const buildDiffEntityDetailsTitleClass =
  'm-0 font-serif text-[0.9375rem] font-semibold leading-snug text-[var(--inv-text)]';

export const buildDiffEntitySectionClass = 'mb-5 last:mb-0';

export const buildDiffEntitySectionTitleClass =
  'mb-3 flex flex-wrap items-center gap-2 text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-[var(--inv-text-muted)]';

export const buildDiffEntitySectionCountClass =
  'rounded-md border border-solid border-[var(--inv-border-soft)] bg-[var(--inv-surface-muted)] px-2 py-0.5 text-[0.6875rem] font-normal normal-case tracking-normal text-[var(--inv-text-secondary)]';

export const buildDiffEntityListClass =
  'overflow-hidden rounded-lg border border-solid border-[var(--inv-border)] bg-[var(--inv-surface)] shadow-[var(--inv-shadow,0_1px_2px_rgba(43,33,24,0.04))]';

export const buildDiffEntityRowClass =
  'flex items-center justify-between gap-3 border-0 border-b border-solid border-[var(--inv-border-soft)] px-3 py-2.5 last:border-b-0';

export const buildDiffEntityRowNameClass =
  'font-mono text-[0.8125rem] text-[var(--inv-text)]';

export const buildDiffEntityRowTypeClass =
  'mt-0.5 block text-[0.75rem] text-[var(--inv-text-muted)]';

export const buildDiffEntityHiddenNoteClass =
  'rounded-lg border border-dashed border-[var(--inv-border-soft)] bg-[var(--inv-surface-muted)] px-3 py-2.5 text-[0.8125rem] leading-snug text-[var(--inv-text-secondary)]';
